import { useQueryClient } from '@tanstack/react-query';
import { getRouteApi, useRouter } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { AuthorGuard } from '@/components/layout/author-guard';
import { Switch } from '@/components/new-ui/switch';
import { StatusType } from '@/features/estates/constants/status-type';
import { getProjectQueryOptions, useProject } from '../api/projects';
import { usePublishProject, useUnpublishProject } from '../api/publish-project';

const projectRoute = getRouteApi(
  '/_authenticated/_sidebar-layout/projects/$projectId/_dashboard-layout'
);

/** Publish switch in the project page header, like the estate page has. */
export const ProjectTopRightFunction = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { projectId } = projectRoute.useParams();
  const { data: project } = useProject({ id: projectId });

  // The overview reads the route loader data, the header reads the query: refresh both
  const refresh = () => {
    router.invalidate();
    return queryClient.invalidateQueries({ queryKey: getProjectQueryOptions(projectId).queryKey });
  };

  const { mutate: publishProject } = usePublishProject({
    mutationConfig: {
      onSuccess: () => {
        toast.success(t('publish-project-success'));
        return refresh();
      },
    },
  });
  const { mutate: unpublishProject } = useUnpublishProject({
    mutationConfig: {
      onSuccess: () => {
        toast.success(t('unpublish-project-success'));
        return refresh();
      },
    },
  });

  const handleChangeSwitch = (checked: boolean) => {
    if (checked) {
      publishProject(projectId);
    } else {
      unpublishProject(projectId);
    }
  };

  // No switch until the project is loaded, otherwise it flashes as unpublished
  if (!project) return null;

  return (
    <AuthorGuard metadata={project.metadata}>
      <div className="flex items-center gap-2 text-sm font-medium">
        <Switch
          id="project-publish-switch"
          checked={project.status === StatusType.published}
          onCheckedChange={handleChangeSwitch}
        />
        <label htmlFor="project-publish-switch" className="cursor-pointer">
          {t('publish')}
        </label>
      </div>
    </AuthorGuard>
  );
};
